'use client';

import { c as _c } from "react/compiler-runtime";
import formatRelativeTime from '@shared/utils/helpers/formatRelativeTime';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Laptop } from 'lucide-react';
import { useState } from 'react';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import Confirmation from '../common/Confirmation';

/**
 * Laptops signed in with `factiii-runner login`. Each token acts as you on
 * runner jobs only; revoking one signs that laptop out on its next call.
 * Hidden while there are none, so Sessions reads as before for most people.
 */
const RunnerCliTokens = () => {
  const $ = _c(16);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["runnerCliTokens"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const queryKey = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey,
      queryFn: _temp
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data
  } = useQuery(t1);
  const [revoking, setRevoking] = useState(null);
  let t2;
  if ($[2] !== queryClient) {
    t2 = {
      mutationFn: _temp2,
      onSuccess: () => {
        setRevoking(null);
        queryClient.invalidateQueries({
          queryKey
        });
      },
      onError: _temp3
    };
    $[2] = queryClient;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const revoke = useMutation(t2);
  if (!data || data.tokens.length === 0) {
    return null;
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div><h3 className="text-lg font-semibold">Runner CLI</h3><p className="text-sm text-textSecondary/70">Laptops that can run runner jobs as you. They never get a shell or a secret value.</p></div>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== data.tokens) {
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = t => <li key={t.id} className="flex items-center gap-3 px-4 py-3"><Laptop size={18} className="shrink-0 text-textSecondary" /><div className="min-w-0 flex-1"><p className="truncate text-sm text-textPrimary">{t.label}</p><p className="text-xs text-textSecondary">Signed in {formatRelativeTime(new Date(t.createdAt))}{t.lastUsedAt && ` · last used ${formatRelativeTime(new Date(t.lastUsedAt))}`}</p></div><Button text="Revoke" variant="SECONDARY" onClick={() => setRevoking({
          id: t.id,
          label: t.label
        })} /></li>;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    t4 = data.tokens.map(t5);
    $[5] = data.tokens;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <ul className="divide-y divide-borderPrimary/40 rounded-xl border border-borderPrimary/40">{t4}</ul>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== revoke || $[11] !== revoking) {
    t6 = revoking && <Confirmation title="Revoke this laptop?" message={`${revoking.label} will need to run factiii-runner login again.`} actionButtonText="Revoke" variant="DANGER" actionButtonLoading={revoke.isPending} cancelHandler={() => setRevoking(null)} confirmHandler={() => revoke.mutate(revoking.id)} />;
    $[10] = revoke;
    $[11] = revoking;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <div className="mt-8 flex flex-col gap-3">{t3}{t5}{t6}</div>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
export default RunnerCliTokens;
function _temp() {
  return unwrapFromResult(client.runnerCli.tokens.query());
}
function _temp2(id) {
  return unwrapFromResult(client.runnerCli.revoke.mutate({
    id
  }));
}
function _temp3(err) {
  return toast(err instanceof Error ? err.message : "Revoke failed", "", "error");
}